import {
  Building2,
  BadgeCheck,
  Activity,
  BarChart3,
  CalendarClock,
  Settings2,
  Briefcase,
  ChevronDown,
  ClipboardList,
  Contact,
  FileSignature,
  FileText,
  FolderOpen,
  Gamepad2,
  Gift,
  LayoutGrid,
  LifeBuoy,
  MapPin,
  SendHorizontal,
  Settings,
  ShieldCheck,
  Target,
  TrendingUp,
  UserCheck,
  UserCog,
  Users,
  Wifi,
  Zap,
  UsersRound,
  DoorOpen,
  Newspaper,
  ServerCog,
  Wallet,
  Receipt,
  Boxes,
  ShoppingCart,
  Waypoints,
  ListTree,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useLocation, useNavigationType } from 'react-router-dom';
import { ActiveMarker } from './ActiveMarker';
import { NavFlyout } from './NavFlyout';
import type { Capability } from '@jt/domain';
import { useSession } from '../lib/session';
import {
  activeSectionFor,
  browserStorage,
  restoreSection,
  saveSection,
  sectionAfterNavigation,
  sessionMarker,
  shouldOpenOnNavigation,
  toggleSection,
} from '../lib/nav-menu';

/**
 * The sidebar.
 *
 * It had grown to twenty-two flat items, which on a laptop meant scrolling the
 * navigation to reach Settings — and the thing about a scrolling sidebar is
 * that people stop believing the bottom of it exists.
 *
 * Six groups now, named after the job rather than the module: what is happening
 * today, who we serve, selling, the network, what the numbers say, and the
 * system itself. Each collapses.
 *
 * It works like the billing system's menu — an accordion — and four rules
 * make that worth having rather than annoying (the logic is in
 * `lib/nav-menu.ts`, where each rule has a test):
 *
 *  1. **Everything is shut when somebody signs in.** Six headings, nothing
 *     else, so the first look at the menu is a table of contents.
 *  2. **One section at a time.** Opening a heading shuts whichever was open,
 *     so the menu never grows back into the twenty-two-row list it replaced.
 *  3. **Going to a page opens its section**, so the item just chosen is never
 *     hidden behind a shut heading. A shut section that holds the current page
 *     is marked as well, so "where am I" is answered even then.
 *  4. **A reload leaves it as it was.** Remembered per person and per browser,
 *     stamped with the session, and forgotten on sign-in and sign-out.
 *
 * And, unchanged: **a group with nothing visible in it renders nothing at
 * all** — not an empty heading. Capabilities already hide individual items,
 * and a technician should not see a "System" heading with no rows under it.
 */

type Item = {
  to: string;
  label: string;
  icon: typeof ClipboardList;
  /** Hidden without it. The API enforces the same rule independently. */
  capability?: Capability;
  /**
   * A preference that has to be on as well, if the item has one.
   *
   * Separate from `capability` because they are different questions. A
   * capability is what a role may do; a preference is what an admin has chosen
   * to put in front of it. Drills are the first of these: `quiz:play` is held
   * by every role and stays that way, and Settings decides who is offered
   * them.
   */
  preference?: 'drillsVisible';
  /** Matches only the exact path — for '/', which would otherwise match all. */
  exact?: boolean;
};

/**
 * A group carries its own icon.
 *
 * Six words down the side of a navy rail read as a list of words. An icon gives
 * each category a shape you can aim at without reading, which is the whole
 * point of grouping — and it is what makes a collapsed rail navigable rather
 * than merely short.
 */
type Group = { id: string; label: string; icon: typeof ClipboardList; items: Item[] };

const GROUPS: Group[] = [
  {
    id: 'today',
    label: 'Today',
    icon: CalendarClock,
    items: [
      // A customer's own page. First, because for the one role that has it, it
      // is the entire application.
      { to: '/', label: 'Board', icon: LayoutGrid, capability: 'ticket:read', exact: true },
      { to: '/list', label: 'All tickets', icon: ClipboardList, capability: 'ticket:read' },
      { to: '/my-work', label: 'My work', icon: Briefcase, capability: 'attendance:own' },
    ],
  },
  /*
   * People.
   *
   * Attendance, leave, expenses and the roster existed and were reachable, but
   * only as "Field team" tucked under Work — so an admin looking for anything
   * to do with staff found a dispatch menu and concluded the system had none.
   * The HR role is built on exactly these four screens; giving them a heading
   * that says what they are is the difference between a feature and a rumour.
   */
  {
    id: 'people',
    label: 'People',
    icon: UserCheck,
    items: [
      { to: '/field-team', label: 'Attendance & leave', icon: UserCheck, capability: 'attendance:read_all' },
      { to: '/technicians', label: 'Roster', icon: Users, capability: 'technician:read' },
      /*
       * Employment contracts, under People and not under Customers.
       *
       * Guarded by `staff_contract:own`, which everybody holds — so this is
       * the one entry in the menu that a technician sees for the same reason
       * an administrator does: their own contract is behind it. The screen
       * shows the roster of everybody's only to whoever may read them.
       */
      { to: '/staff-contracts', label: 'Employment contracts', icon: FileSignature, capability: 'staff_contract:own' },
      // Everybody hands these in, so everybody can reach the page.
      { to: '/documents', label: 'My documents', icon: FolderOpen, capability: 'document:own' },
    ],
  },
  {
    id: 'customers',
    label: 'Customers',
    icon: Contact,
    items: [
      { to: '/customers', label: 'Customers', icon: Contact, capability: 'customer:read' },
      { to: '/contracts', label: 'Contracts', icon: FileSignature, capability: 'contract:read' },
      // The wording new contracts are drafted from. Reading is contract:read;
      // saving needs contract:template, which only an admin holds.
      { to: '/contracts/templates', label: 'Contract wording', icon: FileText, capability: 'contract:read' },
      /*
       * Under Customers rather than People: a landlord is the other side of
       * a commercial conversation, not somebody who works here — and the
       * agreement usually comes out of signing up the block.
       */
      {
        to: '/landlord-agreements',
        label: 'Building access',
        icon: Building2,
        capability: 'landlord_agreement:read',
      },
    ],
  },
  {
    id: 'sales',
    label: 'Selling',
    icon: TrendingUp,
    items: [
      { to: '/leads', label: 'Leads', icon: Target, capability: 'lead:read' },
      { to: '/referrals', label: 'Referrals', icon: Gift, capability: 'referral:read' },
      { to: '/sales', label: 'Sales report', icon: TrendingUp, capability: 'report:sales' },
    ],
  },
  {
    id: 'network',
    label: 'Network',
    icon: Wifi,
    items: [
      { to: '/network', label: 'Network', icon: Wifi, capability: 'network:read' },
      { to: '/network/design', label: 'Network design', icon: Waypoints, capability: 'network:read' },
      { to: '/areas', label: 'Areas', icon: MapPin, capability: 'area:read' },
    ],
  },
  /*
   * Money and stock.
   *
   * A category of its own rather than folded into "Numbers", because these are
   * things people *do* rather than things they read: raising the month's
   * invoices, chasing an overdue account, loading a van on a Monday morning.
   * The reports about them live under Numbers; the work lives here.
   */
  {
    id: 'money',
    label: 'Money & stock',
    icon: Wallet,
    items: [
      { to: '/billing', label: 'Billing', icon: Receipt, capability: 'billing:read' },
      { to: '/invoices', label: 'Invoices', icon: FileText, capability: 'billing:read' },
      { to: '/arrears', label: 'Arrears', icon: Wallet, capability: 'report:arrears' },
      // Money leaving the business on a schedule, which is the same authority
      // as raising an invoice — see the power module's own note on this.
      { to: '/power', label: 'Power & tokens', icon: Zap, capability: 'billing:read' },
      // `stock:read_own` rather than `stock:read`: a technician holds only the
      // narrower one, and the page shows them their own van.
      { to: '/stock', label: 'Stock', icon: Boxes, capability: 'stock:read_own' },
      // The list of items, as opposed to how much of each there is.
      { to: '/stock/list', label: 'Stock list', icon: ClipboardList, capability: 'stock:edit_list' },
      { to: '/purchasing', label: 'Purchasing', icon: ShoppingCart, capability: 'purchase:read' },
    ],
  },
  {
    id: 'insight',
    label: 'Numbers',
    icon: BarChart3,
    items: [
      { to: '/dashboard', label: 'Dashboard', icon: BarChart3, capability: 'report:read' },
      { to: '/reports', label: 'Reports', icon: FileText, capability: 'report:read' },
      { to: '/audit', label: 'Audit log', icon: ShieldCheck, capability: 'audit:read' },
    ],
  },
  {
    id: 'system',
    label: 'System',
    icon: Settings2,
    items: [
      { to: '/status', label: 'System status', icon: Activity, capability: 'system:status' },
      {
        to: '/deliveries',
        label: 'Failed sends',
        icon: SendHorizontal,
        capability: 'message:resend',
      },
      { to: '/users', label: 'Users', icon: UserCog, capability: 'user:manage' },
      // Next to Users, because it is the other half of the same job: who exists,
      // and what each of them is allowed to do.
      { to: '/access', label: 'Groups and roles', icon: UsersRound, capability: 'user:manage' },
      // People waiting at the door. Next to the other two because an admin
      // arriving to sort out who can do what looks in one place, not three.
      { to: '/access-requests', label: 'Access requests', icon: DoorOpen, capability: 'user:manage' },
      /*
       * The reference data every other screen is built from — job types,
       * zones, suppliers, stores and vans. Under System rather than beside
       * each picker, because you come here when a *list* is wrong, which is
       * a different errand from using it.
       */
      { to: '/lists', label: 'Lists', icon: ListTree, capability: 'settings:read' },
      { to: '/settings', label: 'Settings', icon: Settings, capability: 'settings:read' },
      {
        to: '/responsibilities',
        label: 'Role responsibilities',
        icon: BadgeCheck,
        capability: 'settings:write',
      },
      // Beside Settings, because it is Settings — the half addressed to whoever
      // maintains the system rather than whoever runs the business.
      { to: '/developer', label: 'Developer options', icon: ServerCog, capability: 'settings:write' },
      // Everybody. It is a link to a news article.
      { to: '/news', label: 'Tech news', icon: Newspaper, capability: 'news:read' },
      /*
       * `quiz:play` is held by every role, so the capability is not what hides
       * this — the preference is. Settings decides which roles are offered the
       * drills, and for a long time that toggle did nothing at all because
       * nothing read it.
       */
      {
        to: '/drills',
        label: 'Drills',
        icon: Gamepad2,
        capability: 'quiz:play',
        preference: 'drillsVisible',
      },
      { to: '/help', label: 'Help', icon: LifeBuoy },
    ],
  },
];

export function Nav({
  onNavigate,
  collapsed = false,
}: {
  onNavigate?: () => void;
  /**
   * Icons only, no labels.
   *
   * The groups all render as flyouts in this mode rather than pinning open,
   * because there is no width left to pin them into — which is the behaviour
   * the rail already had for unpinned groups, so a collapsed rail is not a
   * second navigation to keep working, it is the existing one narrowed.
   */
  collapsed?: boolean;
}) {
  const { can, drillsVisible, user } = useSession();

  /*
   * Keyed by the same names the items use, so adding a preference is one line
   * in the item and one here rather than a new branch in the filter.
   */
  const preferences = useMemo(() => ({ drillsVisible }), [drillsVisible]);
  const { pathname } = useLocation();
  const navigationType = useNavigationType();
  /**
   * The list the sliding marker lives inside, and therefore the element it must
   * be measured against.
   *
   * Not the `<nav>`: that carries 12px of padding, and measuring from it while
   * positioning inside the list put every placement 12px low.
   */
  const railRef = useRef<HTMLUListElement | null>(null);

  /**
   * One ref per category row, so the portalled flyout can measure where to sit.
   *
   * A plain object of refs rather than one ref per render, because the rows are
   * a mapped list and hooks cannot be called inside a loop. Populated lazily on
   * first render of each row.
   */
  const rowRefs = useRef<Record<string, React.RefObject<HTMLElement | null>>>({});
  const rowRef = (id: string) => {
    rowRefs.current[id] ??= { current: null };
    return rowRefs.current[id]!;
  };

  /** Groups with at least one item this account is both allowed and offered. */
  const visible = useMemo(
    () =>
      GROUPS.map((group) => ({
        ...group,
        items: group.items.filter(
          (item) =>
            (!item.capability || can(item.capability)) &&
            (!item.preference || preferences[item.preference]),
        ),
      })).filter((group) => group.items.length > 0),
    [can, preferences],
  );

  /** Which group holds the page being looked at. */
  const activeGroup = useMemo(() => activeSectionFor(visible, pathname), [visible, pathname]);

  /*
   * The one open section, or none.
   *
   * Started from what this session left — nothing, straight after signing in —
   * and never from the current page: a fresh sign-in shows every heading shut,
   * and a reload shows the menu exactly as it was left.
   */
  const marker = sessionMarker(user);
  const [open, setOpen] = useState<string | null>(() =>
    restoreSection(
      browserStorage(),
      user?.id,
      marker,
      visible.map((group) => group.id),
    ),
  );

  /* Somebody else signed in without a reload: start from their menu, not ours. */
  const lastMarker = useRef(marker);
  useEffect(() => {
    if (lastMarker.current === marker) return;
    lastMarker.current = marker;
    setOpen(restoreSection(browserStorage(), user?.id, marker));
  }, [marker, user?.id]);

  /* Going somewhere opens the section that holds it. Not on the first page. */
  const firstPage = useRef(true);
  useEffect(() => {
    const isFirst = firstPage.current;
    firstPage.current = false;
    if (!shouldOpenOnNavigation(isFirst, navigationType)) return;
    setOpen((current) => sectionAfterNavigation(current, activeGroup));
    // Keyed on the page, not on the group: a capability arriving late must
    // not reopen a section somebody has just shut.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname]);

  useEffect(() => {
    saveSection(browserStorage(), user?.id, marker, open);
  }, [open, user?.id, marker]);

  const toggle = (id: string) => setOpen((current) => toggleSection(current, id));

  /*
   * Keep the page you are on where you can see it.
   *
   * The rail scrolls independently of the page, so after navigating from a
   * link near the bottom of an expanded group — or arriving back on a route
   * from somewhere else — the highlighted item can be outside the rail's own
   * viewport. The menu then says nothing about where you are, which is most
   * of what a menu is for.
   *
   * `nearest` rather than `center`: an item already visible must not move,
   * because a menu that re-scrolls itself under the cursor on every
   * navigation is worse than one that never scrolls at all.
   */
  useEffect(() => {
    const active = railRef.current?.querySelector('[aria-current="page"]');
    if (!active) return;

    active.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
  }, [pathname, open]);

  return (
    /*
     * Scrolls, now that it can.
     *
     * This was `overflow-visible` because the flyout was an absolutely
     * positioned child and a scroll container would have clipped it. The flyout
     * is portalled to the body, so that constraint is gone — and it had to go,
     * because a rail that stays put on a long page needs to be able to scroll
     * its own contents when every category is expanded.
     *
     * The bottom padding is deliberate and generous. The last category used to
     * sit hard against the foot of the rail with nothing beneath it, so on a
     * short window there was no way to tell whether anything followed and the
     * final row was awkward to reach. Room to scroll past the end is what makes
     * a long menu feel finished rather than cut off.
     */
    <nav
      className={`rail-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain pb-24 ${
        collapsed ? 'px-1.5 py-3' : 'p-3'
      }`}
    >
      {/*
        `relative` so the sliding marker positions against the list rather than
        the viewport, and so it scrolls with the rows it tracks.
      */}
      <ul ref={railRef} className="relative space-y-0.5">
        <ActiveMarker scope={railRef} />
        {visible.map((group) => {
          // In the icon rail nothing pins open — there is no width to pin into —
          // so every section is a flyout there, as it always was.
          const isOpen = !collapsed && open === group.id;
          // Shut, but holding the page you are on: say so on the heading.
          const holdsPage = group.id === activeGroup && !isOpen;
          const listId = `nav-section-${group.id}`;
          const GroupIcon = group.icon;

          return (
            /**
             * `group` is Tailwind's hover-scope marker, and `focus-within`
             * carries the same behaviour to the keyboard.
             *
             * Hover **reveals**, it does not navigate and it does not persist —
             * so it is an accelerator rather than the only way in. Clicking
             * still pins the group open, which is what makes this work on a
             * phone, where there is no hover at all, and for anybody driving
             * the rail with Tab.
             */
            <li key={group.id} className="relative">
              <button
                ref={rowRef(group.id) as React.RefObject<HTMLButtonElement>}
                type="button"
                onClick={() => toggle(group.id)}
                /*
                 * Not sticky.
                 *
                 * It was, briefly, so the heading of a long group stayed put
                 * while its items scrolled. The cost was worse than the
                 * problem: items sliding under an opaque heading read as a
                 * fade, and a menu that appears to dissolve as you scroll it
                 * is more unsettling than one you have to scroll back up.
                 *
                 * The active item is scrolled into view on navigation, which
                 * answers "where am I" without moving anything while
                 * somebody reads.
                 */
                className={`nav-section-heading relative flex min-h-tap w-full items-center
                  rounded-lg py-2 text-left transition-colors hover:bg-navy-600 hover:text-white
                  group-focus-within:bg-navy-600 ${
                    holdsPage || isOpen ? 'text-white' : 'text-navy-100'
                  } ${collapsed ? 'justify-center px-0' : 'gap-3 px-3'}`}
                /*
                 * Expanded and controls only where the button really does
                 * expand something in place. In the icon rail it opens a
                 * flyout on hover and focus instead, and claiming
                 * `aria-expanded="false"` for a list that can never appear in
                 * place would be a promise the control does not keep.
                 */
                aria-expanded={collapsed ? undefined : isOpen}
                aria-controls={collapsed ? undefined : listId}
                data-holds-page={holdsPage || undefined}
              >
                <GroupIcon className="h-[18px] w-[18px] shrink-0" aria-hidden="true" />

                {/*
                  `sr-only` rather than removed.
                  A button whose only content is a decorative icon has no
                  accessible name at all, so a collapsed rail would read to a
                  screen reader as eight buttons called nothing. Hiding the text
                  visually keeps the name and costs nothing.
                */}
                <span className={collapsed ? 'sr-only' : 'flex-1'}>
                  {group.label}
                  {holdsPage && <span className="sr-only"> (the current page is in here)</span>}
                </span>

                {/* The page you are on is behind this heading. A dot, so the
                    eye finds it without reading. */}
                {holdsPage && (
                  <span
                    className={`h-1.5 w-1.5 shrink-0 rounded-full bg-white ${
                      collapsed ? 'absolute right-2 top-2' : ''
                    }`}
                    aria-hidden="true"
                  />
                )}

                {/* The count on a shut group, so collapsing never feels like
                    losing track of what is inside it. No room for it when the
                    rail itself is collapsed. */}
                {!isOpen && !collapsed && (
                  <span className="font-mono text-xs tabular-nums text-navy-300">
                    {String(group.items.length).padStart(2, '0')}
                  </span>
                )}

                {!collapsed && (
                  <ChevronDown
                    className={`h-4 w-4 shrink-0 text-navy-300 transition-transform ${
                      isOpen ? '' : '-rotate-90'
                    }`}
                    aria-hidden="true"
                  />
                )}
              </button>

              {/*
                Open: the items sit in the rail, as a list. Rendered only while
                open — the sliding marker and the scroll-into-view both look
                for the active link inside the rail, and a hidden copy of it
                would give them something with no size to aim at.
              */}
              {isOpen && (
                <ul
                  id={listId}
                  aria-label={group.label}
                  className="ml-3 mt-0.5 space-y-0.5 border-l border-navy-600 pl-3"
                >
                  {group.items.map((item) => (
                    <NavItem key={item.to} item={item} onNavigate={onNavigate} />
                  ))}
                </ul>
              )}

              {/*
                The icon rail: a flyout on hover and focus, portalled out of
                the rail, because there is no width to open a section into.

                Only there. In the full rail the accordion is the way in, and
                the two fought: clicking a heading moves every heading below
                it, so whichever slid under a still pointer popped its flyout
                over the section just opened.
              */}
              {collapsed && (
                <NavFlyout label={group.label} trigger={rowRef(group.id)}>
                  {group.items.map((item) => (
                    <NavItem key={item.to} item={item} onNavigate={onNavigate} />
                  ))}
                </NavFlyout>
              )}
            </li>
          );
        })}
      </ul>
    </nav>
  );
}

/**
 * One destination.
 *
 * Shared by the pinned list and the flyout so the two cannot drift into looking
 * like different products — the same size, the same weight, the same active
 * treatment in both places.
 */
function NavItem({ item, onNavigate }: { item: Item; onNavigate?: () => void }) {
  const { to, label, icon: Icon, exact } = item;

  return (
    <li>
      <NavLink
        to={to}
        end={exact}
        onClick={onNavigate}
        /*
         * `relative z-10` puts every link above the sliding marker, which is at
         * z-0 behind them. Without it the marker would paint over the label it
         * is meant to sit behind.
         *
         * No `bg-card` on the active link any more: the marker is that
         * background. Two of them would mean the old highlight appearing
         * instantly at the destination while the new one was still travelling.
         */
        className={({ isActive }) =>
          `relative z-10 flex min-h-tap items-center gap-3 rounded-lg px-3 py-2 text-sm
           font-medium transition-colors ${
             isActive ? 'text-navy-700' : 'text-navy-100 hover:bg-navy-600 hover:text-white'
           }`
        }
      >
        <Icon className="h-4 w-4 shrink-0" aria-hidden="true" />
        <span className="flex-1">{label}</span>
      </NavLink>
    </li>
  );
}
